import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OrgContextService } from '../core/services/org-context.service';
import { ProductAccessService } from '../core/services/product-access.service';
import { StaffContextService } from '../core/services/staff-context.service';

/**
 * Escape hatch for the terminal error pages (/no-organization, /no-access):
 * if the org context did load, send the user back into the app instead of
 * leaving them stranded on a stale error page.
 */
export function redirectToAppIfOrgLoaded(): CanActivateFn {
  return () => {
    const orgContext = inject(OrgContextService);
    const router = inject(Router);
    return orgContext.currentOrg() ? router.parseUrl('/dashboard') : true;
  };
}

export function requireOrg(): CanActivateFn {
  return async () => {
    const orgContext = inject(OrgContextService);
    const router = inject(Router);

    const org = orgContext.currentOrg();
    return org ? true : router.parseUrl('/no-organization');
  };
}

/**
 * The org must be entitled to accord: `product-access/config.enabledProducts`
 * contains 'accord' (written at provisioning — see PLAN.md isolation rule).
 *
 * Entitlement is not enforced for now — every org gets access. Flip
 * ENFORCE_ACCORD_ENTITLEMENT to true to restore gating.
 */
const ENFORCE_ACCORD_ENTITLEMENT = false;

export function requireAccordAccess(): CanActivateFn {
  return async () => {
    const orgContext = inject(OrgContextService);
    const staffContext = inject(StaffContextService);
    const router = inject(Router);

    // Members without 'accord.read' never see the owner app. Send them to the
    // staff app when they're staff — either via the 'accord.staff' role
    // permission, or via a venue staffIndex link (an invited member added to
    // the roster keeps their plain 'member' role; the staffIndex is what makes
    // them staff). Without this, a joined employee lands on an empty dashboard.
    if (!(await orgContext.checkPermission('accord.read'))) {
      if (await orgContext.checkPermission('accord.staff')) {
        return router.parseUrl('/staff');
      }
      try {
        await staffContext.ensureLoaded();
        if (staffContext.venues().length > 0) return router.parseUrl('/staff');
      } catch {
        // Callable failed — fall through to the owner app rather than lock out.
      }
    }

    if (!ENFORCE_ACCORD_ENTITLEMENT) return true;

    const productAccess = inject(ProductAccessService);

    const org = orgContext.currentOrg();
    if (!org) return router.parseUrl('/no-organization');

    const enabled = await productAccess.isAccordEnabled(org.id);
    if (enabled) return true;
    // Denied — but staff members still get the staff app (punch/messages).
    const isStaff = await orgContext.checkPermission('accord.staff');
    return router.parseUrl(isStaff ? '/staff' : '/no-access');
  };
}

/**
 * Gate for the /staff app: allowed when the org role grants 'accord.staff',
 * or when the staff context resolves at least one venue where the caller is
 * indexed (staffIndex-based access needs no org permission at all).
 */
export function requireAccordStaff(): CanActivateFn {
  return async () => {
    const orgContext = inject(OrgContextService);
    const staffContext = inject(StaffContextService);
    const router = inject(Router);

    if (!orgContext.currentOrg()) {
      return router.parseUrl('/no-organization');
    }

    if (await orgContext.checkPermission('accord.staff')) return true;

    try {
      await staffContext.ensureLoaded();
      if (staffContext.venues().length > 0) return true;
    } catch {
      // Callable failed — treat as no staff access.
    }
    return router.parseUrl('/no-access');
  };
}

export function requirePermission(permission: string, fallbackUrl = '/dashboard'): CanActivateFn {
  return async () => {
    const orgContext = inject(OrgContextService);
    const router = inject(Router);

    if (!orgContext.currentOrg()) {
      return router.parseUrl('/no-organization');
    }

    const hasPermission = await orgContext.checkPermission(permission);
    return hasPermission || router.parseUrl(fallbackUrl);
  };
}
